import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Stack,
  Chip,
  Alert,
  Grid,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const ProjectsTabV2 = ({ projects = [], onUpdate, onDirtyChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localProjects, setLocalProjects] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    setLocalProjects([...projects]);
  }, [projects]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => setIsEditing(true);

  const handleSave = () => {
    onUpdate(localProjects);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleCancel = () => {
    setLocalProjects([...projects]);
    setIsEditing(false);
    setIsDirty(false);
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleAddNew = () => {
    setEditForm({
      title: '',
      description: '',
      tags: [],
      url: '',
      start_date: '',
      end_date: ''
    });
    setEditingIndex(localProjects.length);
  };

  const handleEditItem = (index) => {
    setEditForm({ ...localProjects[index] });
    setEditingIndex(index);
  };

  const handleSaveItem = () => {
    const updated = [...localProjects];
    if (editingIndex < updated.length) {
      updated[editingIndex] = editForm;
    } else {
      updated.push(editForm);
    }
    setLocalProjects(updated);
    setEditingIndex(null);
    setEditForm(null);
    setIsDirty(true);
  };

  const handleCancelItem = () => {
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleDeleteItem = (index) => {
    if (window.confirm('Delete this project?')) {
      const updated = localProjects.filter((_, i) => i !== index);
      setLocalProjects(updated);
      setIsDirty(true);
    }
  };

  const renderItemForm = () => (
    <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {editingIndex < localProjects.length ? 'Edit Project' : 'Add Project'}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Project Title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              placeholder="e.g., Machine Learning Pipeline"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Describe the project, your role, and impact..."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL"
              value={editForm.url}
              onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
              placeholder="https://github.com/..."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date"
              value={editForm.start_date}
              onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
              placeholder="e.g., Jan 2023"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date"
              value={editForm.end_date}
              onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
              placeholder="e.g., Dec 2023 or Present"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={editForm.tags?.join(', ') || ''}
              onChange={(e) => setEditForm({
                ...editForm,
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
              placeholder="e.g., python, ml, aws"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelItem} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveItem} variant="contained" startIcon={<SaveIcon />}>
            Save Item
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderViewItem = (project, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{project.title}</Typography>
              {project.url && (
                <Link href={project.url} target="_blank" rel="noopener">
                  <OpenInNewIcon fontSize="small" />
                </Link>
              )}
            </Box>

            {(project.start_date || project.end_date) && (
              <Typography variant="body2" color="text.secondary">
                {project.start_date} {project.start_date && project.end_date && '- '} {project.end_date}
              </Typography>
            )}

            <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
              {project.description}
            </Typography>

            {project.tags && project.tags.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {project.tags.map((tag, i) => (
                  <Chip key={i} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            )}
          </Box>
          {isEditing && (
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => handleEditItem(index)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteItem(index)} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Projects</Typography>
        {!isEditing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Box>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to commit them.
        </Alert>
      )}

      {isEditing && editingIndex === null && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} sx={{ mb: 2 }}>
          Add Project
        </Button>
      )}

      {editingIndex !== null && editForm && renderItemForm()}

      {localProjects.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No projects added yet.
          {isEditing && ' Click "Add Project" to get started.'}
        </Typography>
      ) : (
        localProjects.map((project, index) =>
          editingIndex === index ? null : renderViewItem(project, index)
        )
      )}
    </Box>
  );
};

export default ProjectsTabV2;
